import { datadogRum } from '@datadog/browser-rum';
import { mergeRumAttributes } from './rumContext';

export type RumDomain = 'claim_intake' | 'advisor_chat' | 'agent_chat';

export type RumOperationOutcome = 'yielded' | 'empty' | 'error' | 'skipped' | 'aborted';

export type RunWithRumTrackingParams<T> = Readonly<{
  domain: RumDomain;
  operation: string;
  /** Dify event name, upload purpose, etc. */
  event?: string;
  startAttributes?: Record<string, unknown>;
  run: () => Promise<T>;
  completeAttributes?: (result: T) => Record<string, unknown>;
  errorAttributes?: (error: unknown) => Record<string, unknown>;
  /** When the run returns without throwing but did not execute work (dedupe, missing session). */
  skippedAttributes?: (result: T) => Record<string, unknown> | null;
}>;

let datadogRumReady = false;
let rumConsentGranted = false;

function isRumProductionRuntime(): boolean {
  try {
    return import.meta.env.PROD === true;
  } catch {
    return false;
  }
}

/** Call once from the host app immediately after `datadogRum.init()`. */
export function markDatadogRumReady(): void {
  datadogRumReady = true;
  rumConsentGranted = true;
}

/** Pause or resume custom RUM actions after init, matching the settings switch. */
export function setRumTelemetryConsent(granted: boolean): void {
  rumConsentGranted = granted;
}

/** True after host `datadogRum.init` in production while the RUM setting is on. */
export function isRumTelemetryActive(): boolean {
  if (!datadogRumReady || !rumConsentGranted) return false;
  return isRumProductionRuntime();
}

function nextRunToken(): number {
  return Date.now();
}

function serializeRumError(error: unknown): string {
  if (error instanceof Error) return error.message.slice(0, 500);
  if (typeof error === 'string') return error.slice(0, 500);
  if (error != null && typeof error === 'object' && 'error' in error) {
    const inner = (error as { error: unknown }).error;
    if (typeof inner === 'string') return inner.slice(0, 500);
  }
  try {
    return JSON.stringify(error).slice(0, 500);
  } catch {
    return 'unknown_error';
  }
}

function actionName(domain: RumDomain, operation: string, phase: 'invoked' | 'completed'): string {
  return `${domain}.${operation}.${phase}`;
}

/**
 * Runs an async operation and always emits matching `.invoked` / `.completed` RUM actions.
 * Completion is recorded in `finally` so a missing manual complete call cannot happen.
 */
export async function runWithRumTracking<T>(params: RunWithRumTrackingParams<T>): Promise<T> {
  if (!isRumTelemetryActive()) {
    return params.run();
  }

  const runToken = nextRunToken();
  const baseStart = mergeRumAttributes({
    run_token: runToken,
    ...(params.event ? { event: params.event } : {}),
    ...(params.startAttributes ?? {}),
  });

  const invokedName = actionName(params.domain, params.operation, 'invoked');
  datadogRum.addAction(invokedName, baseStart);

  let outcome: RumOperationOutcome = 'error';
  let completeExtra: Record<string, unknown> = {};

  try {
    const result = await params.run();
    const skipped = params.skippedAttributes?.(result);
    if (skipped) {
      outcome = 'skipped';
      completeExtra = skipped;
    } else {
      completeExtra = params.completeAttributes?.(result) ?? {};
      outcome =
        completeExtra.outcome === 'empty' || completeExtra.data_yielded === false
          ? 'empty'
          : 'yielded';
    }
    return result;
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      outcome = 'aborted';
      completeExtra = { ...(params.errorAttributes?.(error) ?? {}), error_message: 'aborted' };
    } else {
      outcome = 'error';
      completeExtra = {
        ...(params.errorAttributes?.(error) ?? {}),
        error_message: serializeRumError(error),
      };
    }
    throw error;
  } finally {
    const completedName = actionName(params.domain, params.operation, 'completed');
    const completedAttrs = mergeRumAttributes({
      run_token: runToken,
      outcome,
      ...(params.event ? { event: params.event } : {}),
      ...completeExtra,
    });
    datadogRum.addAction(completedName, completedAttrs);
  }
}

/** Fire-and-forget prerequisite / bootstrap check (no paired async work). */
export function recordRumPrerequisiteCheck(
  domain: RumDomain,
  checkName: string,
  attributes: Record<string, unknown>,
): void {
  if (!isRumTelemetryActive()) return;
  const action = `${domain}.prerequisites.checked`;
  const attrs = mergeRumAttributes({ check: checkName, ...attributes });
  datadogRum.addAction(action, attrs);
}

export function trackClaimIntake<T>(
  params: Omit<RunWithRumTrackingParams<T>, 'domain'>,
): Promise<T> {
  return runWithRumTracking({ domain: 'claim_intake', ...params });
}

export function trackAgentChat<T>(
  params: Omit<RunWithRumTrackingParams<T>, 'domain'>,
): Promise<T> {
  return runWithRumTracking({ domain: 'agent_chat', ...params });
}

export function trackAdvisorChat<T>(
  params: Omit<RunWithRumTrackingParams<T>, 'domain'>,
): Promise<T> {
  return runWithRumTracking({ domain: 'advisor_chat', ...params });
}

export function rumSessionIdYieldAttributes(
  sessionId: string | null | undefined,
): Record<string, unknown> {
  return {
    session_id: sessionId ?? null,
    data_yielded: Boolean(sessionId),
  };
}
